import { useContext, useRef, useState } from "react";
import axios from "axios";
import { DocumentDownload, DocumentUpload } from "iconsax-react";
import { useTranslation } from "react-i18next";
import DBModal from "../../component/global/DBModal";
import DBButton from "../../component/global/DBButton";
import DBSelect from "../../component/global/DBSelect";
import { ToastContext } from "../../component/global/ToastContext";
import { COMPANY_IMPORT_MEMBERS } from "../../helperData/ApiAddress";
import { downloadOrgMembersTemplate } from "./orgUsersTemplateDownload";

const TEMPLATE_FORMAT = {
    CSV: "csv",
    XLSX: "xlsx",
};

function importErrorMessage(error, fallback) {
    const data = error?.response?.data;
    if (!data) return fallback;
    if (typeof data.detail === "string") return data.detail;
    if (typeof data.file === "string") return data.file;
    if (Array.isArray(data.file)) return data.file.join(" ");
    if (Array.isArray(data.errors)) return data.errors.join(" ");
    return fallback;
}

export default function OrgUsersBulkModal({ companyId, onHide, onImported }) {
    const { t } = useTranslation();
    const toast = useContext(ToastContext);
    const fileInputRef = useRef(null);
    const [templateFormat, setTemplateFormat] = useState(TEMPLATE_FORMAT.CSV);
    const [selectedFile, setSelectedFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);

    const formatOptions = [
        {
            value: TEMPLATE_FORMAT.CSV,
            label: t("Organizational.Profile.OrgUsers.Template.Csv"),
        },
        {
            value: TEMPLATE_FORMAT.XLSX,
            label: t("Organizational.Profile.OrgUsers.Template.Xlsx"),
        },
    ];

    const handleDownloadTemplate = () => {
        try {
            downloadOrgMembersTemplate(templateFormat);
        } catch {
            toast(
                t("Organizational.Profile.OrgUsers.Template.Error"),
                "danger"
            );
        }
    };

    const handleFilePick = (event) => {
        const file = event.target.files?.[0] || null;
        setSelectedFile(file);
        event.target.value = "";
    };

    const handleSubmit = () => {
        if (!selectedFile) {
            toast(
                t("Organizational.Profile.OrgUsers.Bulk.FileRequired"),
                "danger"
            );
            return;
        }
        setImportLoading(true);
        const formData = new FormData();
        formData.append("file", selectedFile);
        if (companyId) {
            formData.append("company", companyId);
        }
        axios
            .post(COMPANY_IMPORT_MEMBERS, formData, {
                headers: {
                    Authorization: `Bearer ${sessionStorage.getItem("auth")}`,
                },
            })
            .then((r) => {
                const data = r.data || {};
                toast(
                    t("Organizational.Profile.OrgUsers.Bulk.Success", {
                        created: data.created ?? 0,
                        updated: data.updated ?? 0,
                        skipped: data.skipped ?? 0,
                    }),
                    "success"
                );
                onImported?.();
                onHide?.();
            })
            .catch((e) =>
                toast(
                    importErrorMessage(
                        e,
                        t("Organizational.Profile.OrgUsers.Bulk.Error")
                    ),
                    "danger"
                )
            )
            .finally(() => setImportLoading(false));
    };

    return (
        <DBModal
            permanent
            show
            minWidth={640}
            onHide={onHide}
            className="org-users-bulk-modal"
        >
            <DBModal.Header
                closeBtn={onHide}
                title={t("Organizational.Profile.OrgUsers.Bulk.Title")}
            />
            <DBModal.Content>
                <div className="org-users-bulk-body">
                    <p className="org-users-bulk-help">
                        {t("Organizational.Profile.OrgUsers.Bulk.Help")}
                    </p>

                    <div className="org-users-bulk-toolbar">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            hidden
                            onChange={handleFilePick}
                        />
                        <div className="org-users-format-control">
                            <label htmlFor="org-users-template-format">
                                {t(
                                    "Organizational.Profile.OrgUsers.Template.Format"
                                )}
                            </label>
                            <DBSelect
                                id="org-users-template-format"
                                size="large"
                                aria-label={t(
                                    "Organizational.Profile.OrgUsers.Template.Format"
                                )}
                                value={templateFormat}
                                onChange={setTemplateFormat}
                                options={formatOptions}
                                style={{ minWidth: 120 }}
                            />
                        </div>
                        <DBButton
                            type="secondary"
                            outlined
                            caption={
                                <>
                                    <DocumentDownload
                                        size={18}
                                        aria-hidden="true"
                                    />{" "}
                                    {t(
                                        "Organizational.Profile.OrgUsers.Template.Download"
                                    )}
                                </>
                            }
                            onClick={handleDownloadTemplate}
                        />
                        <DBButton
                            type="secondary"
                            outlined
                            caption={
                                <>
                                    <DocumentUpload
                                        size={18}
                                        aria-hidden="true"
                                    />{" "}
                                    {t(
                                        "Organizational.Profile.OrgUsers.Bulk.Upload"
                                    )}
                                </>
                            }
                            onClick={() => fileInputRef.current?.click()}
                        />
                    </div>

                    {selectedFile ? (
                        <div
                            className="org-users-selected-file"
                            translate="no"
                        >
                            {selectedFile.name}
                        </div>
                    ) : (
                        <p className="org-users-bulk-hint">
                            {t(
                                "Organizational.Profile.OrgUsers.Bulk.FileHint"
                            )}
                        </p>
                    )}
                </div>
            </DBModal.Content>
            <DBModal.Footer>
                <DBButton
                    caption={t("Organizational.Profile.Cancel.Button")}
                    type="secondary"
                    onClick={onHide}
                />
                <DBButton
                    caption={t(
                        "Organizational.Profile.OrgUsers.Bulk.Submit"
                    )}
                    type="primary"
                    loading={importLoading}
                    onClick={handleSubmit}
                />
            </DBModal.Footer>
        </DBModal>
    );
}
